import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../css/others/Card.css";
import "../css/others/Table.css";
import BackButton from "../Reusablecomponents/BackButton";
import { apiCall } from "../main/ApiCall";
import ToastPopup from "../popups/ToastPopup";
import { FunctionCampaignDetailsInfo, FunctionCampaignType, createToastMessage, ToastMessage } from '../interFaces/interfaces';
import { TimezoneOption } from "../interFaces/interfaces";
const CreateFunctionCampaign: React.FC = () => {
  const [campaign, setCampaign] = useState<FunctionCampaignDetailsInfo>({ campaignName: "", campaignType: "FUNCTION", auditType: "", auditObjective: "", auditScope: "", auditTeam: "", location: "", frequency: "", campaignStartDate: "", campaignEndDate: "" });
  const [auditTypes, setAuditTypes] = useState<FunctionCampaignType[]>([]);
  const [timezones, setTimezones] = useState<TimezoneOption[]>([]);
  const [loading, setLoading] = useState({ auditTypes: true, timezones: true });
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [frequencies, setFrequencies] = useState<string[]>([]);
  const clientId = localStorage.getItem("clientId");
  const createdBy = localStorage.getItem("email");
  const navigate = useNavigate();
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  const restrictedChars = /[\\:*?"<>|/]/;
  const containsSpecialChars = (str: string) => {
    return restrictedChars.test(str);
  };
  useEffect(() => {
    const fetchEnums = async () => {
      try {
        const auditRes = await apiCall("get", "/api/enums/AuditTypeEnum");
        setAuditTypes(auditRes);
        const timezoneRes = await apiCall("get", "/api/timezones");
        setTimezones(timezoneRes);
        const frequencyRes = await apiCall("get", "/api/dropdown-config/function");
        setFrequencies(frequencyRes.values);
        setLoading({ auditTypes: false, timezones: false });
      } catch (error) {
        setMessage(createToastMessage("Failed to load options", "error", 3000));
        setLoading({ auditTypes: false, timezones: false });
      }
    };
    fetchEnums();
  }, []);
  const isWeekend = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 || day === 6;
  };
  const handleInputChange = (key: keyof FunctionCampaignDetailsInfo, event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (key === "campaignType") return;
    const { value } = event.target;
    if (["campaignName", "auditObjective", "auditScope", "auditTeam"].includes(String(key))) {
      if (value.length > 60) {
        setMessage(createToastMessage("Maximum 60 characters allowed", "warning", 3000));
        return;
      }
      if (containsSpecialChars(value)) {
        setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
        return;
      }
    }
    if (key === "campaignStartDate" || key === "campaignEndDate") {
      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0);
      if (isWeekend(value)) {
        setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
        return;
      }
      if (key === "campaignStartDate") {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        if (selectedDate < tomorrow) {
          setMessage(createToastMessage("Start date must be tomorrow or later", "error", 3000));
          return;
        }
        if (campaign.campaignEndDate && new Date(campaign.campaignEndDate) < selectedDate) {
          setCampaign({ ...campaign, [key]: value, campaignEndDate: "" });
          return;
        }
      } else if (key === "campaignEndDate") {
        if (!campaign.campaignStartDate) {
          setMessage(createToastMessage("Please select start date first", "warning", 3000));
          return;
        }
        if (selectedDate < new Date(campaign.campaignStartDate)) {
          setMessage(createToastMessage("End date must be after start date", "error", 3000));
          return;
        }
      }
    }
    setCampaign({ ...campaign, [key]: value });
  };
  const formatDateTime = (date: string, isStartDate: boolean = false) => {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      let time = "23:59:59";
      if (isStartDate) {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        time = `${hours}:${minutes}:${seconds}`;
      }
      return `${year}-${month}-${day} ${time}`;
    }
    return "";
  };
  const handleSubmit = async () => {
    const isEmpty = Object.values(campaign).some((value) => value === "");
    if (isEmpty) {
      setMessage(createToastMessage("Please fill all fields.", "warning", 3000));
      return;
    }
    if (campaign.campaignName.length > 50) {
      setMessage(createToastMessage("Function Campaign Name cannot exceed 50 characters", "warning", 3000));
      return;
    }
    const textFields: (keyof FunctionCampaignDetailsInfo)[] = ["campaignName", "auditObjective", "auditScope", "auditTeam"];
    for (const field of textFields) {
      const fieldValue = campaign[field];
      if (typeof fieldValue === 'string' && containsSpecialChars(fieldValue)) {
        const fieldLabel = `Function ${String(field)
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str: string) => str.toUpperCase())}`;
        setMessage(createToastMessage(`Special characters are not allowed in ${fieldLabel}`, "error", 3000));
        return;
      }
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(campaign.campaignStartDate);
    startDate.setHours(0, 0, 0, 0);
    if (isWeekend(campaign.campaignStartDate)) {
      setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
      return;
    }
    const endDate = new Date(campaign.campaignEndDate);
    endDate.setHours(0, 0, 0, 0);
    if (isWeekend(campaign.campaignEndDate)) {
      setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
      return;
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    if (startDate < tomorrow) {
      setMessage(createToastMessage("Start date must be tomorrow or later", "warning", 3000));
      return;
    }
    if (endDate <= startDate) {
      setMessage(createToastMessage("End date must be after start date", "warning", 3000));
      return;
    }
    try {
      const createdDate = formatDateTime(new Date().toISOString(), true);
      const formattedCampaign = { ...campaign, createdBy, clientId, createdDate, campaignStartDate: formatDateTime(campaign.campaignStartDate, true), campaignEndDate: formatDateTime(campaign.campaignEndDate), campaignType: "FUNCTION" };
      await apiCall("post", "/api/function/campaigns", formattedCampaign);
      setMessage(createToastMessage("Function Campaign created successfully!", "success", 3000));
      setTimeout(() => { navigate(-1) }, 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to create campaign";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const tomorrowDate = getTomorrowDate();
  const minEndDate = campaign.campaignStartDate || tomorrowDate;
  return (
    <div className="container">
      <h1 className="title">Create Function Campaign</h1>
      <div className="grid">
        {Object.entries(campaign).map(([key, value]) => {
          const label = `Function ${key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}`;
          if (key === "campaignType") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <input type="text" value="FUNCTION" readOnly />
                  </div>
                </div>
              </div>
            );
          }
          if (key === "auditType") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <select value={value} onChange={(event) => handleInputChange(key as keyof FunctionCampaignDetailsInfo, event)}>
                      <option value="">Select {label}</option>
                      {loading.auditTypes ? (
                        <option disabled>Loading audit types...</option>
                      ) : (
                        auditTypes.map((type) => (
                          <option key={type.value} value={type.value}>{type.description}</option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>
            );
          }
          if (key === "location") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <select value={value} onChange={(event) => handleInputChange(key as keyof FunctionCampaignDetailsInfo, event)} >
                      <option value="">Select Timezone</option>
                      {loading.timezones ? (
                        <option disabled>Loading timezones...</option>
                      ) : (
                        timezones.map((tz) => (
                          <option key={tz.id} value={tz.id}>
                            {tz.id} ({tz.abbreviation}, {tz.offset})
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>
            );
          }
          if (key === "frequency") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <select value={value} onChange={(event) => handleInputChange(key as keyof FunctionCampaignDetailsInfo, event)}>
                      <option value="">Select Frequency</option>
                      {frequencies.map((freq) => (
                        <option key={freq} value={freq}>{freq}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          }
          if (key === "campaignStartDate" || key === "campaignEndDate") {
            return (
              <div className="card" key={key}>
                <div className="card-inner">
                  <div className="content">
                    <div className="label">{label}</div>
                    <input type="date" value={value} min={key === "campaignStartDate" ? tomorrowDate : minEndDate} onChange={(event) => handleInputChange(key as keyof FunctionCampaignDetailsInfo, event)} />
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div className="card" key={key}>
              <div className="card-inner">
                <div className="content">
                  <div className="label">{label}</div>
                  <input type="text" value={value} onChange={(event) => handleInputChange(key as keyof FunctionCampaignDetailsInfo, event)} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="buttons">
        <BackButton className="back-button2" label="Back" />
        <button className="submit-button2" onClick={handleSubmit}>Submit</button>
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default CreateFunctionCampaign;